<script setup lang="ts">
import { computed, onMounted, ref, toRaw, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { cloneDeep } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import type { CartAnimal } from '../../../../interfaces';
import { useADialog, useAToast } from '@Atoms';
import { getPayloadDateString, getPayloadDateTimeString, getPayloadEventDateString, scrollToTop } from '@Helpers';
import { useSessionStore } from '@Stores';
import { GetAnimalAdoptionFees, GetAnimalOwnerRequestedEuthanasiaFees } from '@AnimalProfileModule';
import { useForm } from '@FormModule';
import { useCartSteps } from '../../../../composables';
import { OUTCOMES_WITH_VOUCHERS } from '../../../../constants';
import { CART_STEPS } from '../../../../enums';
import {
  AddOutcomeToCart,
  CompleteOutcomeService,
  GetOutcomeFormByName,
  GetOutcomeVoucherOptionsService,
  GetWaiversService,
  DeleteCartItem,
  DeleteCartSessionService,
} from '../../../../services';
import useShoppingCartStore from '../../../../store';
import getSanitizedPayload from './getSanitizedPayload';
import useFields from './useFields';
import useFieldsOutcomeDate from './useFieldsOutcomeDate';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const {
  animals,
  cartData,
  cartId,
  cartLength,
  completeOutcomeRoute,
  donations,
  isAdoption,
  isEuthanasia,
  isIntake,
  isTransferOut,
  loading,
  mainLoading,
  outcomeForm,
  outcomeInfoActiveStep,
  productsLength,
  rtoFees,
  servicesLength,
  stepState,
  triggerCompleteOutcomeSignal,
} = storeToRefs(useShoppingCartStore());
const { shelterProfile, userProfile } = storeToRefs(useSessionStore());
const { outcomeCompletedCartReset, resetCart } = useShoppingCartStore();

const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, nextStep, nextStepButton, setOutcomeFlow, TRANSFER_OUT_MOBILE_STEPS } = useCartSteps();
const { fieldsValidation } = useForm();
const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();
const {
  additionalFeesOptions,
  adoptionFeesOptions,
  animalOutcomeVouchers,
  euthanasiaFeesOptions,
  currentDataOnStepperId,
  currentSchemaModels,
  fieldsSchema,
  formError,
  outcomeSchemaModels,
  sourceData,
  voucherTemplates,
  waivers,
  setAdditionalFeeField,
  setForm,
} = useFields();
const {
  currentSchemaModels: currentSchemaModelsOutcomeDate,
  fieldsSchema: fieldsSchemaOutcomeDate,
  setForm: setFormOutcomeDate,
  userCanSetPaidDate,
} = useFieldsOutcomeDate();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const isSubmitting = ref(false);

const animalCartStepId = computed(() => animals.value[outcomeInfoActiveStep.value]._id);

const onlyAnimalsCartLengh = computed(
  () => cartLength.value - productsLength.value - servicesLength.value - donations.value.length
);

const currentAnimalData = computed(() =>
  animals.value.find(({ _id }: CartAnimal) => _id === currentDataOnStepperId.value)
);

const canIssueVouchers = computed(
  () =>
    !!shelterProfile.value?.enable_outcome_vouchers &&
    OUTCOMES_WITH_VOUCHERS.includes(cartData.value?.outcome_type || '')
);

onMounted(async () => await onInitPage());

watch(() => animals.value.length, clampOutcomeStepToAnimalsCount, { immediate: true });
watch(animalCartStepId, () => onAnimalCartStepIdChange());
watch(currentDataOnStepperId, () => getUpdatedFeesOptions());
watch(triggerCompleteOutcomeSignal, () => triggerCompleteOutcomeSignal.value && onNextStep());

// HELPERS
function getAdoptionFees() {
  GetAnimalAdoptionFees(currentAnimalData?.value?.species).then((result: any) => {
    adoptionFeesOptions.value = result.map((fee: any) => {
      let amount = fee.amount;

      if (typeof amount === 'number') {
        const parts = amount.toString().split('.');
        if (!parts[1] || parts[1].length < 2) {
          amount = amount.toFixed(2);
        }
      }

      return {
        ...fee,
        amount,
        label: `${fee.description} - $${amount}`,
      };
    });
  });
}

function getEuthanasiaFees() {
  GetAnimalOwnerRequestedEuthanasiaFees(currentAnimalData?.value?.species).then((result: any) => {
    euthanasiaFeesOptions.value = result.map((fee: any) => {
      return { ...fee, label: `${fee.description} - $${fee.amount}` };
    });
  });
}

function setCurrentDataStepperId() {
  const animal = animals.value[outcomeInfoActiveStep.value];
  if (!animal?._id) return;

  currentDataOnStepperId.value = animal._id;
}

function clampOutcomeStepToAnimalsCount(animalsCount: number) {
  if (animalsCount === 0) return;
  if (outcomeInfoActiveStep.value >= animalsCount) outcomeInfoActiveStep.value = animalsCount - 1;
}

async function setOutcomeForms() {
  if (!cartData.value.outcome_type) return;
  loading.value = true;

  setCurrentDataStepperId();

  await loadOutcomeForms()
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

async function loadOutcomeForms() {
  await GetWaiversService('outcome', cartData.value.outcome_type).then((waiver: any) => (waivers.value = waiver));

  if (canIssueVouchers.value) await setOutcomeVoucherOptions();

  const outcomeFormData = await GetOutcomeFormByName(cartData.value.outcome_type);
  outcomeForm.value = outcomeFormData;

  const outcomeFormNotReady = !outcomeForm.value?.form?.fields;
  if (outcomeFormNotReady) return notify(TOAST_ERROR);

  // NOTE: We use this rtoFees state to repopulate the select RTO Fees field after hard refresh
  const hasRtoFees = outcomeForm.value?.rto_fees.length;
  if (hasRtoFees) rtoFees.value = outcomeForm.value.rto_fees;

  sourceData.value = {
    rawFields: outcomeForm.value?.form.fields,
    additional_fees_options: outcomeForm.value?.additional_fees,
    partners: outcomeForm.value?.partners,
    rto_fees: outcomeForm.value?.rto_fees,
  };

  setFormOutcomeDate();
  setForm();
}

function validateForms() {
  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  const noOutcomeDate = fieldsValidation(fieldsSchemaOutcomeDate.value, currentSchemaModelsOutcomeDate.value);

  if (hasErrors || noOutcomeDate) return (formError.value = true);

  const rawSet = toRaw(currentSchemaModels.value);
  const currentSchemaModelsClone = new Set(Array.from(rawSet).map((obj) => cloneDeep(toRaw(obj))));

  outcomeSchemaModels.value[outcomeInfoActiveStep.value] = currentSchemaModelsClone;

  formError.value = false;
}

function getUpdatedFeesOptions() {
  if (isAdoption.value) getAdoptionFees();
  if (isEuthanasia.value && isIntake.value) getEuthanasiaFees();
}

function flushStepState(payload: any) {
  emits('onStepUpdate', nextStep.value.step!.name);
  stepState.value![CART_STEPS.OUTCOME_INFO] = payload;
  loading.value = false;
}

// EVENTS
function setOutcomeVoucherOptions() {
  const PAYLOAD = {
    animal_ids: animals.value.map((animal: CartAnimal) => String(animal.animal_id ?? animal._id)),
  };

  return GetOutcomeVoucherOptionsService(PAYLOAD)
    .then(({ data }) => {
      voucherTemplates.value = data.voucher_templates;
      animalOutcomeVouchers.value = data.animal_vouchers;
    })
    .catch(() => notify(TOAST_ERROR));
}

function onAnimalCartStepIdChange() {
  setCurrentDataStepperId();
  getUpdatedFeesOptions();
  setAdditionalFeeField(additionalFeesOptions.value);
}

async function onInitPage() {
  outcomeInfoActiveStep.value = 0;
  currentStep.value = CART_STEPS.OUTCOME_INFO;
  await setOutcomeForms();
}

function onNextAnimal(action: (...args: never[]) => void) {
  if (!fieldsSchema.value.length) return notify(TOAST_ERROR);

  validateForms();

  if (formError.value) return;
  scrollToTop();
  action();
}

function onNextStep() {
  if (isSubmitting.value) return;

  // NOTE :: With no form loaded, validation passes on nothing and the api saves a blank outcome as a real one.
  // The checkout selector turns `loading` on before it signals this step, so it has to go off here.
  if (!fieldsSchema.value.length) {
    loading.value = false;

    return notify(TOAST_ERROR);
  }

  validateForms();

  if (formError.value) return (loading.value = false);

  isSubmitting.value = true;
  loading.value = true;

  const [outcomeDate, paidDate = { value: null }] = currentSchemaModelsOutcomeDate.value;
  const dateTimeOutcome = getPayloadDateTimeString(outcomeDate.value);

  const payload = animals.value.map((animal: any, index: number) => {
    const outcome = getSanitizedPayload(outcomeSchemaModels.value[index]);

    // NOTE: Removing rto_fees. Not used in the backend.
    delete (outcome as Partial<typeof outcome>).rto_fees;
    const { vouchers, waivers, ...outcomeData } = outcome;

    return {
      ...animal,
      outcome_date: dateTimeOutcome,
      intake_date: animal.intake_date ? getPayloadDateString(animal.intake_date) : null,
      paid_date: paidDate.value ? getPayloadEventDateString(paidDate.value) : null,
      waivers,
      vouchers: vouchers ?? [],
      outcome: {
        ...outcomeData,
        animal_id: animal._id,
        animal_name: animal.animal_name,
        form_id: outcomeForm.value.form._id.$oid,
        form_title: outcomeForm.value.form.form_title,
        outcome_type: cartData.value.outcome_type,
        outcome_date: dateTimeOutcome,
        partner_name: outcomeForm.value.partners.find((field: any) => field.id === outcome.partner_name),
      },
    };
  });

  AddOutcomeToCart(payload)
    .then((data) => {
      cartData.value = data;

      if (isTransferOut.value) return onCompleteTransferOutRequest(data, payload);

      if (!nextStep.value.step) return onCompleteOutcome(data);

      flushStepState(payload);
    })
    .catch(() => notify(TOAST_ERROR) && (loading.value = false))
    .finally(() => (isSubmitting.value = false));
}

function onCompleteTransferOutRequest(cartDataPayload: any, payload: any) {
  if (!triggerCompleteOutcomeSignal.value?.isMobile) return onCompleteOutcome(cartDataPayload);

  setOutcomeFlow(TRANSFER_OUT_MOBILE_STEPS, 'guardian');

  flushStepState(payload);
}

function onCompleteOutcome(payload: any) {
  // NOTE :: TS compliance - completeOutcomeRoute should always exist when completing an outcome
  if (!completeOutcomeRoute.value) {
    Sentry.captureException(new Error('completeOutcomeRoute is null during onCompleteOutcome'), {
      tags: { component: 'MCartStepOutcomeInfo', type: 'ts-compliance' },
    });
  }

  activeModal.value = SYSTEM_MODALS.COMPLETE_OUTCOME_LOADING_MESSAGE;
  dialogVisibility.value = true;

  CompleteOutcomeService(payload)
    .then(() => push(completeOutcomeRoute.value!).then(() => outcomeCompletedCartReset()))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}

function onRemoveAnimalRequest(animal: CartAnimal, index: number) {
  activeModal.value = SYSTEM_MODALS.CART_REMOVE_ANIMAL;
  activeModalProps.value = {
    animal,
    index,
    isLastAnimal: animals.value.length === 1,
    onConfirm: () => onRemoveAnimal(index),
  };
  dialogVisibility.value = true;
}

async function onRemoveAnimal(index: number) {
  const TOAST_ERROR = {
    message: 'cart.toast.removeAnimal.error.message',
    title: 'cart.toast.removeAnimal.error.title',
    type: TOAST_TYPES.INFO,
  };

  const TOAST_SUCCESS = {
    title: 'cart.toast.removeAnimal.success.title',
    type: TOAST_TYPES.INFO,
  };

  loading.value = true;

  if (animals.value.length > 1) {
    await DeleteCartItem(index)
      .then(({ data }) => {
        outcomeInfoActiveStep.value = outcomeInfoActiveStep.value > 1 ? outcomeInfoActiveStep.value - 1 : 0;
        cartData.value = data;
        notify(TOAST_SUCCESS);
      })
      .catch(() => notify(TOAST_ERROR))
      .finally(() => (loading.value = false));

    return;
  }

  await DeleteCartSessionService(isIntake.value, cartId.value)
    .then(() => {
      resetCart();
      mainLoading.value = false;
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (loading.value = false));
}
</script>

<template>
  <div class="flex h-full max-w-[700px] flex-col gap-4">
    <FormTemplate
      class="w-full"
      formError=""
      :schema="fieldsSchemaOutcomeDate"
    >
      <Message
        v-if="userCanSetPaidDate"
        pt:text="text-sm"
        severity="info"
        :closable="false"
      >
        {{ $t('cart.label.retroactiveOutcome', { firstName: userProfile?.first_name }) }}
      </Message>
    </FormTemplate>

    <Stepper
      v-model:activeStep="outcomeInfoActiveStep"
      linear
      pt:header="px-0 flex-[0]"
      pt:nav="justify-center flex"
      pt:panelContainer="px-0"
    >
      <StepperPanel
        v-for="(animal, index) in animals"
        :key="animal._id"
      >
        <template #header="{ clickCallback }">
          <div class="flex w-full items-center justify-center gap-2">
            <div
              class="flex cursor-pointer flex-col items-center gap-2"
              @click="clickCallback"
            >
              <AShoppingCartAnimal
                hasDelete
                :animal
                @onRemoveAnimal="onRemoveAnimalRequest(animal, Number(index))"
              />
            </div>
          </div>
        </template>

        <template #content="{ index, nextCallback, prevCallback }">
          <div class="scrollToTop flex flex-col gap-4">
            <div
              class="surface-border border-round surface-ground justify-content-center align-items-center flex flex-auto border-2 border-dashed p-4 font-medium"
            >
              <FormTemplate
                class="w-full"
                :formError="animal._id === animalCartStepId ? formError : false"
                :schema="fieldsSchema"
              />
            </div>

            <AFormError :formError />
          </div>

          <div class="flex py-4">
            <Button
              v-if="onlyAnimalsCartLengh > 1 && index > 0"
              outlined
              class="af-button af-button-success-outlined mr-auto"
              icon="pi pi-arrow-left"
              :label="$t('common.previous')"
              @click="prevCallback"
            />

            <Button
              v-if="index + 1 < onlyAnimalsCartLengh"
              outlined
              class="af-button af-button-success-outlined ml-auto"
              icon="pi pi-arrow-right"
              :label="$t('common.next')"
              @click="onNextAnimal(nextCallback)"
            />

            <Button
              v-else-if="!isTransferOut"
              class="af-button af-button-success ml-auto"
              iconPos="right"
              :label="$t(nextStepButton.label)"
              :loading="isSubmitting"
              @click="onNextStep"
            >
              <template #icon>
                <FaIcon
                  v-if="nextStepButton.icon"
                  class="mr-2"
                  :icon="$icons[nextStepButton.icon]"
                />
              </template>
            </Button>
          </div>
        </template>
      </StepperPanel>
    </Stepper>
  </div>
</template>
